import { useAnalytics } from '@/hooks/useAnalytics';
import { cn } from '@/lib/utils';
import { useBreadcrumbsStore } from '@/store/breadcrumbs';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@opaz/ui-components';
import { Link } from '@tanstack/react-router';
import { ComponentProps, Fragment } from 'react';
import { useTranslation } from 'react-i18next';

type SubNavContainerProps = {
  isLoading?: boolean;
  showOneStepBreadcrumbs?: boolean;
  entityTitle?: string;
} & ComponentProps<'div'>;

export const SubNavContainer = ({
  isLoading = false,
  showOneStepBreadcrumbs = false,
  entityTitle,
  className,
  children,
  ...props
}: SubNavContainerProps) => {
  const { t, i18n } = useTranslation();
  const breadCrumbs = useBreadcrumbsStore((state) => state.breadcrumbs);
  const analytics = useAnalytics();

  const handleTrackBreadcrumbSelect = (title: string) => {
    analytics.trackBreadcrumbSelect(title);
  };

  return (
    <div
      className={cn(
        'flex flex-col items-center min-h-[180px] bg-[linear-gradient(88.9deg,_rgba(97,174,229,0.18)_0.16%,_rgba(198,219,230,0.2)_19.99%,_#EBF3F7_75.14%,_#F6F9FC_99.29%)] border-b border-low border-[#E1E1E2] px-3 py-8',
        className,
      )}
      {...props}
    >
      {!isLoading && (showOneStepBreadcrumbs || breadCrumbs.length > 1) && (
        <div className="max-w-[960px] w-full mb-10">
          <Breadcrumb dir={i18n.dir()}>
            <BreadcrumbList>
              {breadCrumbs.map((item, index) => {
                const translatedTitle = t(item.translationKey as unknown as TemplateStringsArray) as string;

                if (index === breadCrumbs.length - 1) {
                  return (
                    <>
                      <BreadcrumbItem key={`breadcrumb-${index}`}>
                        <BreadcrumbPage>{entityTitle ? entityTitle : translatedTitle}</BreadcrumbPage>
                      </BreadcrumbItem>

                      {showOneStepBreadcrumbs && <BreadcrumbSeparator />}
                    </>
                  );
                }

                return (
                  <Fragment key={`breadcrumb-${index}`}>
                    <BreadcrumbItem>
                      <BreadcrumbLink asChild>
                        <Link to={item.path} onClick={() => handleTrackBreadcrumbSelect(translatedTitle)}>
                          {translatedTitle}
                        </Link>
                      </BreadcrumbLink>
                    </BreadcrumbItem>

                    <BreadcrumbSeparator />
                  </Fragment>
                );
              })}
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      )}

      <div className="flex flex-1 items-center max-w-[960px] w-full">{children}</div>
    </div>
  );
};
